const SCRIPT_STYLE_TAGS_SELECTOR = 'script, style, iframe, object, embed, form, input, textarea, button';

export const hasHtmlMarkup = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ''));

const collapseWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const decodeHtmlEntities = (value) => {
  const raw = String(value || '');
  if (!raw) return '';

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    const decodedDocument = new window.DOMParser().parseFromString(raw, 'text/html');
    return decodedDocument.documentElement?.textContent || decodedDocument.body?.textContent || raw;
  }

  return raw
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, '&');
};

export const resolveRichTextHtml = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (hasHtmlMarkup(raw)) return raw;

  const decodedOnce = decodeHtmlEntities(raw).trim();
  if (decodedOnce && hasHtmlMarkup(decodedOnce)) return decodedOnce;

  const decodedTwice = decodeHtmlEntities(decodedOnce).trim();
  if (decodedTwice && hasHtmlMarkup(decodedTwice)) return decodedTwice;

  return '';
};

export const stripRichTextToPlainText = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const resolvedHtml = resolveRichTextHtml(raw);

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    if (resolvedHtml) {
      const parsedHtml = new window.DOMParser().parseFromString(resolvedHtml, 'text/html');
      return collapseWhitespace(parsedHtml.body?.textContent || '');
    }

    const decoded = decodeHtmlEntities(raw);
    if (hasHtmlMarkup(decoded)) {
      const reparsed = new window.DOMParser().parseFromString(decoded, 'text/html');
      return collapseWhitespace(reparsed.body?.textContent || '');
    }

    return collapseWhitespace(decoded || raw);
  }

  const normalized = resolvedHtml || decodeHtmlEntities(raw) || raw;
  return normalized
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const getRichTextPreview = (value, maxLength = 160) => {
  const plain = stripRichTextToPlainText(value);
  if (!plain) return '';

  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
};

export const sanitizeRichTextHtml = (value) => {
  const rawHtml = resolveRichTextHtml(value);
  if (!rawHtml) return '';

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return rawHtml
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(["']).*?\1/gi, '')
      .replace(/\sstyle\s*=\s*(["']).*?\1/gi, '')
      .replace(/\s(href|src)\s*=\s*(["'])\s*javascript:[\s\S]*?\2/gi, '');
  }

  const parsed = new window.DOMParser().parseFromString(rawHtml, 'text/html');
  parsed.body.querySelectorAll(SCRIPT_STYLE_TAGS_SELECTOR).forEach((node) => node.remove());

  parsed.body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const attrName = attribute.name.toLowerCase();
      const attrValue = String(attribute.value || '').trim();

      if (attrName.startsWith('on')) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (attrName === 'style') {
        element.removeAttribute(attribute.name);
        return;
      }

      if (attrName === 'srcdoc') {
        element.removeAttribute(attribute.name);
        return;
      }

      if ((attrName === 'href' || attrName === 'src') && /^javascript:/i.test(attrValue)) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName === 'A') {
      element.setAttribute('rel', 'noopener noreferrer');
      if (!element.getAttribute('target')) {
        element.setAttribute('target', '_blank');
      }
    }
  });

  return (parsed.body.innerHTML || '').trim();
};
